import { createLead, runDuplicateCheck, submitDuplicateDecision, submitKyc, uploadLeadPhoto } from "./api";
import {
  dequeue,
  findLocalLeadByServerId,
  getLocalLead,
  getPhotoFile,
  listQueue,
  setPhotoState,
  upsertLocalLead,
  type LocalLeadRecord,
  type PhotoWhich,
} from "./store";

/**
 * Replay the offline mutation queue. Best-effort: each item is attempted in
 * order; failures stay in the queue for the next `online` event. create-lead
 * items publish a localId → serverId mapping that dependent queued items
 * (duplicate check/decision, KYC submission) resolve against.
 */

export interface SyncResult {
  attempted: number;
  succeeded: number;
  remaining: number;
}

function resolveServerId(leadRef: string): number | undefined {
  if (/^\d+$/.test(leadRef)) return Number(leadRef);
  return getLocalLead(leadRef)?.serverId;
}

export async function syncQueue(): Promise<SyncResult> {
  const queue = listQueue();
  let succeeded = 0;

  for (const item of queue) {
    try {
      if (item.kind === "create-lead") {
        const res = await createLead(item.payload);
        const local = getLocalLead(item.localId);
        if (local) {
          upsertLocalLead({ ...local, serverId: res.lead.id, status: "synced" });
        }
      } else {
        const serverId = resolveServerId(item.leadRef);
        if (serverId === undefined) continue; // create still pending — retry next sync
        if (item.kind === "duplicate-check") {
          await runDuplicateCheck(serverId);
          const local = findLocalLeadByServerId(serverId);
          if (local && local.status === "pending-duplicate-check") {
            upsertLocalLead({ ...local, status: "synced" });
          }
        } else if (item.kind === "duplicate-decision") {
          await submitDuplicateDecision(serverId, item.payload);
          markDecisionSynced(serverId, item.payload.decision);
        } else if (item.kind === "kyc-submission") {
          await submitKyc(serverId, item.idempotencyKey);
          const local = findLocalLeadByServerId(serverId);
          if (local) upsertLocalLead({ ...local, status: "kyc-submitted" });
        } else if (item.kind === "photo-upload") {
          // Depends on the server lead id (resolved above) and the File in the
          // in-memory registry. File lost (page reload) → leave queued and
          // mark failed so the UI asks the rep to recapture.
          const file = getPhotoFile(item.localId, item.photoType);
          if (!file) {
            setPhotoState(item.localId, item.photoType, "failed");
            continue;
          }
          await syncPhoto(item.localId, serverId, item.photoType, file);
        }
      }
      dequeue(item.queueId);
      succeeded += 1;
    } catch {
      /* keep in queue — retried on next online event */
    }
  }

  return { attempted: queue.length, succeeded, remaining: listQueue().length };
}

function markDecisionSynced(serverId: number, decision: "merge" | "reject" | "continue"): void {
  const local = findLocalLeadByServerId(serverId);
  if (!local) return;
  const status: LocalLeadRecord["status"] =
    decision === "merge" ? "closed-merge" : decision === "reject" ? "closed-reject" : local.status;
  upsertLocalLead({ ...local, status });
}

/**
 * Single photo upload with state transitions (shared by sync replay and the
 * S1 inline retry). Failures leave the state at "failed" — the caller decides
 * whether to keep the item queued for the next online event.
 */
export async function syncPhoto(
  localId: string,
  serverId: number,
  which: PhotoWhich,
  file: File,
): Promise<void> {
  const photoType = which === "customer" ? "customer_photo" : "house_photo";
  setPhotoState(localId, which, "uploading");
  try {
    const res = await uploadLeadPhoto(serverId, photoType, file);
    setPhotoState(localId, which, "uploaded", res.url);
  } catch (err) {
    setPhotoState(localId, which, "failed");
    throw err;
  }
}
